import { useCallback } from 'react'
import { useVisitorData } from '@fingerprint/react'
import { isProduction } from 'utils/environment'

export const FingerprintFlows = {
  LOGIN: 'LOGIN',
  FORGOT_PASSWORD: 'FORGOT_PASSWORD',
  RESET_PASSWORD: 'RESET_PASSWORD',
} as const

type Flow = (typeof FingerprintFlows)[keyof typeof FingerprintFlows]
interface TrackFingerprintProps {
  flow?: Flow
  merchantName: string
  accountId: number
  accountReference: string
  profileId: number
}

const isFingerprintEnabled = isProduction
const FINGERPRINT_SOURCE = 'BVNK Merchant Portal'

export const useFingerprint = () => {
  const { getData, isLoading, error, data } = useVisitorData({ immediate: false })

  const trackFingerprint = useCallback(
    ({
      merchantName,
      accountId,
      accountReference,
      profileId,
      flow = FingerprintFlows.LOGIN,
    }: TrackFingerprintProps) => {
      const enableFingerprint = !!flow && !!merchantName && !!accountId && !!profileId

      if (!enableFingerprint || !isFingerprintEnabled) return

      return getData({
        tags: {
          source: FINGERPRINT_SOURCE,
          flow,
          merchantName,
          accountId,
          accountReference,
        },
      })
    },
    [getData]
  )

  const getFingerprintEventId = useCallback(
    async (flow: Flow): Promise<string> => {
      if (!isFingerprintEnabled) return ''

      // The root provider caches results in localStorage for 24h and the SDK exposes no
      // per-call cache bypass, so a cached response's event_id can be older than the
      // gateway's two-minute acceptance window.
      const result = await getData({
        tags: { source: FINGERPRINT_SOURCE, flow },
      })

      return result?.event_id ?? ''
    },
    [getData]
  )

  return { trackFingerprint, getFingerprintEventId, isLoading, error, data }
}
